/**
 * Organization Admin Users Tab Component
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, Edit, CheckCircle, Cancel } from '@mui/icons-material';
import { organizationAdminApi, OrgAdminUser } from '@/api/organizationAdmin';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';

export const OrganizationAdminUsersTab: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<OrgAdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<OrgAdminUser | null>(null);
  const [formData, setFormData] = useState<{
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
    role: string;
  }>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    is_active: true,
    role: UserRole.USER,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await organizationAdminApi.getUsers();
      setUsers(data || []);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: OrgAdminUser) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        is_active: user.is_active,
        role: user.role || UserRole.USER,
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        is_active: true,
        role: UserRole.USER,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (editingUser) {
        const updateData: any = {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          is_active: formData.is_active,
          role: formData.role,
        };

        // Only include password if it's not empty
        if (formData.password && formData.password.trim() !== '') {
          updateData.password = formData.password;
        }

        await organizationAdminApi.updateUser(editingUser.id, updateData);
      } else {
        // For new users, use the selected role (defaults to USER)
        await organizationAdminApi.createUser({
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          is_active: formData.is_active,
          role: formData.role,
        });
      }
      handleCloseDialog();
      loadUsers();
    } catch (err: any) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save user');
    }
  };

  const handleToggleActive = async (user: OrgAdminUser) => {
    try {
      await organizationAdminApi.toggleUserActive(user.id);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update user');
    }
  };

  // Filter out only global admin users (org admin can see all users in their org, including themselves)
  // But they can only manage USER role users, not admin or organization_admin
  const visibleUsers = users.filter(u => {
    const role = u.role;
    // Only filter out global admin role, keep organization_admin and user roles
    return role !== 'admin' && role !== UserRole.ADMIN;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Users in Your Organization</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Surname</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Loading...</TableCell>
              </TableRow>
            ) : !visibleUsers || visibleUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No users found</TableCell>
              </TableRow>
            ) : (
              visibleUsers.map((user) => {
                const getRoleLabel = (role: string) => {
                  if (role === UserRole.ADMIN || role === 'admin') return 'Admin';
                  if (role === UserRole.ORGANIZATION_ADMIN || role === 'organization_admin') return 'Organization Admin';
                  return 'User';
                };

                const getRoleColor = (role: string) => {
                  if (role === UserRole.ADMIN || role === 'admin') return 'error';
                  if (role === UserRole.ORGANIZATION_ADMIN || role === 'organization_admin') return 'warning';
                  return 'default';
                };

                return (
                  <TableRow key={user.id}>
                    <TableCell>{user.first_name || '-'}</TableCell>
                    <TableCell>{user.last_name || '-'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleLabel(user.role)}
                        color={getRoleColor(user.role) as 'default' | 'warning' | 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  <TableCell align="right">
                    {/* Allow editing all users except global admin */}
                    {user.role !== 'admin' && user.role !== UserRole.ADMIN ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(user)}
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                          disabled={user.role === 'organization_admin' || user.role === UserRole.ORGANIZATION_ADMIN}
                        >
                          {user.is_active ? <CheckCircle color="success" /> : <Cancel />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(user)}
                          title="Edit"
                        >
                          <Edit />
                        </IconButton>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Global admin cannot be edited
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Create User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />
            {!editingUser ? (
              <TextField
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                fullWidth
                required
              />
            ) : (
              <TextField
                label="New Password (leave empty to keep current)"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                fullWidth
                helperText="Leave empty if you don't want to change the password"
              />
            )}
            <TextField
              label="First Name"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value={UserRole.USER}>User</MenuItem>
                <MenuItem value={UserRole.ORGANIZATION_ADMIN}>Organization Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
            {!editingUser && (
              <Alert severity="info">
                New users will be created with the "User" role in your organization. You can change their role later.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.email || (!editingUser && !formData.password)}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
